"use client";

import { useMemo } from "react";
import {
  diatonicChordsForKey,
  type ChordEntry,
} from "@/lib/music/chord-catalog";
import { useChordLabel, useNoteLabel, useNoteLabels } from "@/hooks/use-notation";
import { useTonality } from "@/contexts/studio-context";

const VIOLET = "#8b5cf6";
const VIOLET_BRIGHT = "#c4b5fd";
const VIOLET_SOFT = "rgba(139, 92, 246, 0.12)";
const VIOLET_GLOW = "rgba(139, 92, 246, 0.35)";
const GREEN = "#34d399";
const TEXT = "rgba(255, 255, 255, 0.92)";
const TEXT_DIM = "rgba(255, 255, 255, 0.55)";
const TEXT_MUTE = "rgba(255, 255, 255, 0.32)";
const BORDER = "rgba(255, 255, 255, 0.06)";

const FONT_MONO = "var(--font-mono), 'JetBrains Mono', ui-monospace, monospace";

type Props = {
  /** Active chord, used to highlight the matching row. Omit when browsing
   *  (no chord currently being practiced) so the list shows but nothing is
   *  marked active. */
  entry?: ChordEntry;
  /** Map<chordId, Set<positionId>>, done positions per chord. */
  progress: Record<string, ReadonlySet<string>>;
  onSelect: (entry: ChordEntry) => void;
};

export function PracticeStructureSidebar({ entry, progress, onSelect }: Props) {
  const chordLabel = useChordLabel();
  const toNoteLabel = useNoteLabel();
  const noteLabels = useNoteLabels();
  const [tonality] = useTonality();

  const diatonic = useMemo(() => {
    if (!tonality) {
      return [];
    }

    return diatonicChordsForKey(tonality.tonic, tonality.mode, entry?.id);
  }, [tonality, entry]);

  const keyLabel = useMemo(() => {
    if (!tonality) {
      return null;
    }

    const mode = tonality.mode === "major" ? "majeur" : "mineur";

    return `${toNoteLabel(tonality.tonic)} ${mode}`.toUpperCase();
  }, [tonality, toNoteLabel]);

  if (!tonality) {
    return (
      <div
        style={{
          display: "flex",
          flex: 1,
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "24px 16px",
          textAlign: "center",
          fontSize: 11,
          color: TEXT_MUTE,
          letterSpacing: "0.05em",
          lineHeight: 1.6,
        }}
      >
        Choisis une tonalité dans la barre d&apos;outils pour voir sa structure.
      </div>
    );
  }

  if (diatonic.length === 0) {
    return (
      <div
        style={{
          display: "flex",
          flex: 1,
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "24px 16px",
          textAlign: "center",
          fontSize: 11,
          color: TEXT_MUTE,
          letterSpacing: "0.05em",
          lineHeight: 1.6,
        }}
      >
        Pas d&apos;accords diatoniques pour cette tonalité.
      </div>
    );
  }

  return (
    <div
      style={{
        display: "flex",
        flex: 1,
        flexDirection: "column",
        minHeight: 0,
        background: "var(--color-overlay-surface)",
      }}
    >
      <div
        style={{
          padding: "16px 18px 10px",
          fontSize: 10,
          fontFamily: FONT_MONO,
          color: TEXT_MUTE,
          letterSpacing: "0.18em",
          fontWeight: 600,
          display: "flex",
          justifyContent: "space-between",
          textTransform: "uppercase",
        }}
      >
        <span>Structure</span>
        <span style={{ color: TEXT_DIM }}>{diatonic.length}</span>
      </div>

      <div
        style={{
          flex: 1,
          overflowY: "auto",
          padding: "0 8px",
        }}
      >
        {diatonic.map((d) => {
          // V7d: total = number of inversions exposed (capped at 3 even for tetrads)
          const total = Math.min(d.entry.intervals.length, 3);
          const validated = progress[d.entry.id]?.size ?? 0;
          const pct = total > 0 ? (validated / total) * 100 : 0;
          const isComplete = total > 0 && validated === total;
          const isActive = d.isCurrent;

          return (
            <button
              key={d.entry.id}
              type="button"
              onClick={() => onSelect(d.entry)}
              aria-pressed={isActive}
              style={{
                width: "100%",
                padding: "10px 10px",
                borderRadius: 8,
                background: isActive ? VIOLET_SOFT : "transparent",
                border: `1px solid ${isActive ? VIOLET_GLOW : "transparent"}`,
                marginBottom: 2,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 12,
                fontFamily: "inherit",
                color: TEXT,
                textAlign: "left",
                transition:
                  "background-color 150ms, border-color 150ms",
              }}
            >
              <div
                style={{
                  fontSize: 18,
                  fontWeight: 400,
                  letterSpacing: "-0.01em",
                  color: isActive ? TEXT : TEXT_DIM,
                  minWidth: 36,
                  fontFamily: "Inter, sans-serif",
                  lineHeight: 1,
                }}
              >
                {chordLabel(d.entry.symbol)}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 9,
                    fontFamily: FONT_MONO,
                    color: TEXT_MUTE,
                    letterSpacing: "0.05em",
                    marginBottom: 4,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {noteLabels(d.entry.noteNames).join(" · ")}
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  <div
                    style={{
                      flex: 1,
                      height: 2,
                      background: "rgba(255,255,255,0.06)",
                      borderRadius: 99,
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        width: `${pct}%`,
                        height: "100%",
                        background: isComplete ? GREEN : VIOLET,
                        boxShadow: isActive
                          ? `0 0 6px ${VIOLET_GLOW}`
                          : "none",
                        transition: "width 200ms ease-out",
                      }}
                    />
                  </div>
                  <div
                    style={{
                      fontSize: 9,
                      fontFamily: FONT_MONO,
                      color: isComplete ? GREEN : TEXT_MUTE,
                      minWidth: 24,
                      textAlign: "right",
                    }}
                  >
                    {validated}/{total}
                  </div>
                </div>
              </div>
              <div
                style={{
                  fontFamily: FONT_MONO,
                  fontSize: 18,
                  fontWeight: 600,
                  letterSpacing: "0.04em",
                  color: isActive ? VIOLET_BRIGHT : TEXT_DIM,
                  minWidth: 36,
                  textAlign: "right",
                  lineHeight: 1,
                }}
              >
                {d.roman}
              </div>
            </button>
          );
        })}
      </div>

      <div
        style={{
          padding: "14px 18px 18px",
          marginTop: 8,
          borderTop: `1px solid ${BORDER}`,
          fontSize: 10,
          fontFamily: FONT_MONO,
          color: TEXT_MUTE,
          letterSpacing: "0.1em",
          textAlign: "center",
          textTransform: "uppercase",
        }}
      >
        {keyLabel} · {diatonic.length} accords
      </div>
    </div>
  );
}
